import { fmtCompact, fmtNum } from '../utils/fmt';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { fmtPrice, fmtMktCap, fmtPct } from '../utils/format';
import { fetchWithCache, getFetchMeta } from '../utils/fetchWithCache';
import { pearson, rsiState } from '../utils/indicators';
import { useCoinRSI } from '../utils/rsi';
import SourceDot, { SourceAttr, mergeMeta } from './SourceDot';
import DataSourceDown from './DataSourceDown';

// ── Utilities ──────────────────────────────────────────────────────────────────

/* Pearson correlation of hourly log returns over the 7-day CoinGecko
   sparkline (168 hourly points). Both series are aligned from the end. */
function btcCorrelation(altPrices, btcPrices) {
  const n = Math.min(altPrices?.length ?? 0, btcPrices?.length ?? 0);
  if (n < 24) return null;
  const a = altPrices.slice(-n), b = btcPrices.slice(-n);
  const ra = [], rb = [];
  for (let i = 1; i < n; i++) {
    if (a[i] > 0 && a[i - 1] > 0 && b[i] > 0 && b[i - 1] > 0) {
      ra.push(Math.log(a[i] / a[i - 1]));
      rb.push(Math.log(b[i] / b[i - 1]));
    }
  }
  return pearson(ra, rb);
}

function corrLabel(r) {
  if (r == null) return { text: 'Nincs adat',       color: 'var(--text3)' };
  if (r >= 0.7)  return { text: 'Erősen együtt mozog', color: '#00c853' };
  if (r >= 0.4)  return { text: 'Részben együtt mozog', color: '#f5a623' };
  return                { text: 'Gyengén kapcsolódik',  color: '#ff4d4d' };
}

const STABLECOIN_SYMS = new Set([
  'usdt','usdc','busd','dai','tusd','usdp','gusd','frax','usdd',
  'ust','susd','husd','lusd','fei','mim','usdx','cusd','musd','alusd',
]);

function isStablecoin(coin) {
  return (
    STABLECOIN_SYMS.has(coin.symbol.toLowerCase()) ||
    Math.abs(coin.price_change_percentage_7d_in_currency ?? 0) < 0.3
  );
}

// ── DeFi TVL constants ────────────────────────────────────────────────────────

const DEFI_CHAIN_TARGETS = new Set([
  'ethereum','bsc','solana','arbitrum','optimism','base',
  'avalanche','polygon','tron','near','sui','hyperliquid',
]);
const DEFI_REFRESH = 15 * 60 * 1000;

function fmtTVL(n) {
  if (n == null || isNaN(n)) return '—';
  return fmtCompact(n);
}

// ── Shared: sortable column header ────────────────────────────────────────────

function SortTh({ label, col, sort, onSort, right }) {
  const active = sort.col === col;
  return (
    <th
      className={`scan-th sortable${right ? ' right' : ''}${active ? ' sort-active' : ''}`}
      onClick={() => onSort(col)}
    >
      {label}
      {active && <span className="sort-arrow">{sort.dir === 'asc' ? ' ▲' : ' ▼'}</span>}
    </th>
  );
}

function CoinCell({ coin }) {
  return (
    <div className="scan-coin-cell">
      <img src={coin.image} alt={coin.symbol} className="scan-coin-icon" loading="lazy" onError={e => { e.target.style.display = 'none'; }} />
      <div>
        <div className="scan-coin-sym">{coin.symbol.toUpperCase()}</div>
        <div className="scan-coin-name">{coin.name}</div>
      </div>
    </div>
  );
}

// ── Section 0: Gainers & Losers ───────────────────────────────────────────────

function GainersLosers({ data, meta }) {
  const [period, setPeriod] = useState('24h');

  const { gainers, losers } = useMemo(() => {
    const field = period === '24h'
      ? 'price_change_percentage_24h'
      : 'price_change_percentage_7d_in_currency';
    const sorted = [...data]
      .filter(c => c[field] != null)
      .sort((a, b) => b[field] - a[field]);
    return {
      gainers: sorted.slice(0, 10),
      losers:  sorted.slice(-10).reverse(),
    };
  }, [data, period]);

  return (
    <div className="scan-section">
      <div className="scan-section-header">
        <span className="scan-section-title scan-section-title--gl">NYERTESEK &amp; VESZTESEK<SourceDot source="CoinGecko" meta={meta} /></span>
        <div className="scan-filter-group">
          <button className={`scan-filter-btn ${period === '24h' ? 'active' : ''}`} onClick={() => setPeriod('24h')}>24h</button>
          <button className={`scan-filter-btn ${period === '7d'  ? 'active' : ''}`} onClick={() => setPeriod('7d')}>7d</button>
        </div>
      </div>
      <div className="gl-grid">
        <div>
          <div className="gl-col-title positive">▲ Top 10 Nyertes</div>
          <div className="gl-list">
            {gainers.map((coin, i) => {
              const pct = period === '24h'
                ? coin.price_change_percentage_24h
                : coin.price_change_percentage_7d_in_currency;
              return (
                <div key={coin.id} className="gl-row">
                  <span className="gl-rank">{i + 1}</span>
                  <img src={coin.image} alt={coin.symbol} className="scan-coin-icon" loading="lazy" onError={e => { e.target.style.display = 'none'; }} />
                  <span className="gl-sym">{coin.symbol.toUpperCase()}</span>
                  {pct > 10 && <span className="gl-big-badge gl-big-badge--up">🚀 Nagy mozgás</span>}
                  <span className="gl-pct positive">{fmtPct(pct, 2)}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <div className="gl-col-title negative">▼ Top 10 Vesztes</div>
          <div className="gl-list">
            {losers.map((coin, i) => {
              const pct = period === '24h'
                ? coin.price_change_percentage_24h
                : coin.price_change_percentage_7d_in_currency;
              return (
                <div key={coin.id} className="gl-row">
                  <span className="gl-rank">{i + 1}</span>
                  <img src={coin.image} alt={coin.symbol} className="scan-coin-icon" loading="lazy" onError={e => { e.target.style.display = 'none'; }} />
                  <span className="gl-sym">{coin.symbol.toUpperCase()}</span>
                  {pct < -10 && <span className="gl-big-badge gl-big-badge--down">💥 Nagy mozgás</span>}
                  <span className="gl-pct negative">{fmtNum(pct, 2)}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <SourceAttr cg />
    </div>
  );
}

// ── Section 4: DeFi TVL Tracker ───────────────────────────────────────────────

/* Only /v2/chains (~64 KB). The former second call, /protocols, returned
   ~9 MB / 7 s, did not fit into localStorage and was therefore re-downloaded
   on every visit and every 15 minutes — just for a top-10 protocol table,
   which has been removed. */
const DEFI_CHAINS_URL = 'https://api.llama.fi/v2/chains';

function DeFiTVL() {
  const [chains,    setChains]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [meta,      setMeta]      = useState(null);
  const cacheRef = useRef({ ts: 0 });

  const fetchTVL = useCallback(async () => {
    if (Date.now() - cacheRef.current.ts < DEFI_REFRESH) return;
    try {
      const chainData = await fetchWithCache(DEFI_CHAINS_URL, { cacheDuration: DEFI_REFRESH });
      const filtered = chainData
        .filter(c => DEFI_CHAIN_TARGETS.has(c.name.toLowerCase()))
        .sort((a, b) => b.tvl - a.tvl);
      setChains(filtered);
      setMeta(getFetchMeta(DEFI_CHAINS_URL));
      cacheRef.current.ts = Date.now();
    } catch (_) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTVL(); }, [fetchTVL]);
  useEffect(() => {
    const id = setInterval(fetchTVL, DEFI_REFRESH);
    return () => clearInterval(id);
  }, [fetchTVL]);

  const totalTVL = chains.reduce((s, c) => s + (c.tvl ?? 0), 0);
  const biggest  = chains[0] ?? null;
  const bestDay  = [...chains].filter(c => (c.change_1d ?? 0) > 0).sort((a, b) => (b.change_1d ?? 0) - (a.change_1d ?? 0))[0] ?? null;
  const worstDay = [...chains].filter(c => (c.change_1d ?? 0) < 0).sort((a, b) => (a.change_1d ?? 0) - (b.change_1d ?? 0))[0] ?? null;

  return (
    <div className="scan-section">
      <div className="scan-section-header">
        <span className="scan-section-title scan-section-title--defi">DEFI TVL KÖVETŐ<SourceDot source="DeFiLlama" meta={meta} /></span>
        <button className="scan-refresh-btn" onClick={() => { cacheRef.current.ts = 0; fetchTVL(); }}>↻</button>
      </div>

      {loading ? (
        <div className="scan-state" style={{ padding: '24px 0' }}>
          <div className="scan-spinner">◈</div>
          <div>TVL adatok betöltése…</div>
        </div>
      ) : (
        <>
          <div className="defi-cards">
            <div className="defi-card">
              <div className="defi-card-label">Összes TVL (12 lánc)</div>
              <div className="defi-card-val">{fmtTVL(totalTVL)}</div>
            </div>
            <div className="defi-card">
              <div className="defi-card-label">Legnagyobb Lánc</div>
              <div className="defi-card-val">{biggest?.name ?? '—'}</div>
              <div className="defi-card-sub">{fmtTVL(biggest?.tvl)}</div>
            </div>
            <div className="defi-card">
              <div className="defi-card-label">Legjobb 24h</div>
              <div className="defi-card-val positive">
                {bestDay ? `${bestDay.name} ${fmtPct(bestDay.change_1d, 2)}` : '—'}
              </div>
            </div>
            <div className="defi-card">
              <div className="defi-card-label">Legrosszabb 24h</div>
              <div className="defi-card-val negative">
                {worstDay ? `${worstDay.name} ${fmtNum(worstDay.change_1d, 2)}%` : '—'}
              </div>
            </div>
          </div>

          <div className="scan-table-wrap" style={{ marginBottom: 20 }}>
            <div className="defi-sub-title">Lánc TVL</div>
            <table className="scan-table scan-table--rank">
              <thead>
                <tr>
                  <th className="scan-th">#</th>
                  <th className="scan-th">Lánc</th>
                  <th className="scan-th right">TVL</th>
                  <th className="scan-th right">24h%</th>
                  <th className="scan-th right">7d%</th>
                </tr>
              </thead>
              <tbody>
                {chains.map((c, i) => {
                  const d1  = c.change_1d ?? null;
                  const d7  = c.change_7d ?? null;
                  const pct = biggest && biggest.tvl > 0 ? (c.tvl / biggest.tvl) * 100 : 0;
                  return (
                    <tr key={c.name} className="scan-row">
                      <td className="scan-td muted">{i + 1}</td>
                      <td className="scan-td">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span style={{ fontWeight: 700 }}>{c.name}</span>
                          <div className="defi-tvl-bar-track">
                            <div className="defi-tvl-bar-fill" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="scan-td right" style={{ fontWeight: 700 }}>{fmtTVL(c.tvl)}</td>
                      <td className={`scan-td right ${d1 == null ? '' : d1 >= 0 ? 'positive' : 'negative'}`}>
                        {d1 != null ? `${fmtPct(d1, 2)}` : '—'}
                      </td>
                      <td className={`scan-td right ${d7 == null ? '' : d7 >= 0 ? 'positive' : 'negative'}`}>
                        {d7 != null ? `${fmtPct(d7, 2)}` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        </>
      )}
    </div>
  );
}

// ── Section 1: RSI Scanner ─────────────────────────────────────────────────────

const RSI_SCAN_N = 100;

function RSIScanner({ data, preview = false }) {
  const [sort, setSort] = useState({ col: 'rsi', dir: 'asc' });
  const [filter, setFilter] = useState('all');

  function handleSort(col) {
    setSort(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }));
  }

  // Exactly the top 100 by market cap: the title and the row count match.
  const top = useMemo(() =>
    [...data].sort((a, b) => (a.market_cap_rank ?? 1e9) - (b.market_cap_rank ?? 1e9)).slice(0, RSI_SCAN_N),
  [data]);

  /* FREE preview: this table sits in the blurred area, so no Bybit requests. */
  const { rows: rsiRows, symbols, pending, meta, tickersFailed, failed, retry } = useCoinRSI(top, 'D', { enabled: !preview });

  const enriched = useMemo(() =>
    top.map(c => ({
      ...c,
      rsi: rsiRows[c.id]?.rsi ?? null,
      noPerp: symbols ? !symbols[c.id] : false,
      short: rsiRows[c.id]?.reason === 'short',
      error: rsiRows[c.id]?.reason === 'error',
      // "…" only while a request is really running
      loading: !preview && !tickersFailed && !rsiRows[c.id] && !(symbols && !symbols[c.id]),
    })),
  [top, rsiRows, symbols, preview, tickersFailed]);

  const withRsi         = enriched.filter(c => c.rsi != null).length;
  const noPerpCount     = enriched.filter(c => c.noPerp).length;
  const oversoldCount   = useMemo(() => enriched.filter(c => c.rsi != null && c.rsi < 30).length, [enriched]);
  const overboughtCount = useMemo(() => enriched.filter(c => c.rsi != null && c.rsi > 70).length, [enriched]);

  const rows = useMemo(() => {
    let list = filter === 'oversold'   ? enriched.filter(c => c.rsi != null && c.rsi < 30) :
               filter === 'overbought' ? enriched.filter(c => c.rsi != null && c.rsi > 70) :
               enriched;

    const m = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (sort.col === 'rsi') {
        // Coins without RSI always go to the end
        if (a.rsi == null) return 1;
        if (b.rsi == null) return -1;
        return (a.rsi - b.rsi) * m;
      }
      if (sort.col === 'rank')  return (a.market_cap_rank - b.market_cap_rank) * m;
      if (sort.col === 'price') return (a.current_price - b.current_price) * m;
      if (sort.col === '24h')   return ((a.price_change_percentage_24h ?? 0) - (b.price_change_percentage_24h ?? 0)) * m;
      if (sort.col === '7d')    return ((a.price_change_percentage_7d_in_currency ?? 0) - (b.price_change_percentage_7d_in_currency ?? 0)) * m;
      return 0;
    });
  }, [enriched, filter, sort]);

  return (
    <div className="scan-section">
      <div className="scan-section-header">
        <span className="scan-section-title scan-section-title--rsi">TOP {top.length} RSI SZKENNER · 1D<SourceDot source="CoinGecko (lista, árak) · Bybit perpetual (RSI)" meta={meta} /></span>
        <div className="scan-filter-group">
          <button className={`scan-filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}>Mind ({top.length})</button>
          <button className={`scan-filter-btn oversold ${filter === 'oversold' ? 'active' : ''}`}
            onClick={() => setFilter('oversold')}>Túladott &lt;30 ({oversoldCount})</button>
          <button className={`scan-filter-btn overbought ${filter === 'overbought' ? 'active' : ''}`}
            onClick={() => setFilter('overbought')}>Túlvett &gt;70 ({overboughtCount})</button>
        </div>
      </div>
      <div className="scan-section-sub" style={{ marginBottom: 10, fontSize: 11 }}>
        Valódi RSI 14 (Wilder) a Bybit perpetual lezárt napi gyertyáiból. {withRsi}/{top.length} coinnál van adat
        {pending > 0 && !tickersFailed ? ` · ${pending} még töltődik` : ''}
        {noPerpCount > 0 ? ` · ${noPerpCount} coinnak nincs Bybit USDT perpetualja (pl. stablecoinok, wrapped tokenek)` : ''}
        {failed > 0 ? ` · ${failed} coinnál a Bybit nem válaszolt` : ''}.
      </div>
      {!preview && (tickersFailed || failed > 0) && <DataSourceDown onRetry={retry} />}
      <div className="scan-table-wrap">
        <table className="scan-table scan-table--rank">
          <thead>
            <tr>
              <SortTh label="Rang"    col="rank"  sort={sort} onSort={handleSort} />
              <th className="scan-th">Coin</th>
              <SortTh label="Ár"      col="price" sort={sort} onSort={handleSort} right />
              <SortTh label="RSI 14 · 1D" col="rsi" sort={sort} onSort={handleSort} right />
              <th className="scan-th">Állapot</th>
              <SortTh label="24h%"    col="24h"   sort={sort} onSort={handleSort} right />
              <SortTh label="7d%"     col="7d"    sort={sort} onSort={handleSort} right />
            </tr>
          </thead>
          <tbody>
            {rows.map(coin => {
              const st   = rsiState(coin.rsi);
              const c24h = coin.price_change_percentage_24h ?? 0;
              const c7d  = coin.price_change_percentage_7d_in_currency ?? 0;
              return (
                <tr key={coin.id} className="scan-row">
                  <td className="scan-td muted">#{coin.market_cap_rank}</td>
                  <td className="scan-td"><CoinCell coin={coin} /></td>
                  <td className="scan-td right">{fmtPrice(coin.current_price)}</td>
                  <td className="scan-td right">
                    {coin.rsi != null
                      ? <span className="scan-rsi-val" style={{ color: st.color }}>{fmtNum(coin.rsi, 1)}</span>
                      : <span className="muted" title={coin.noPerp ? 'Nincs Bybit USDT perpetual' : undefined}>
                          {coin.noPerp ? '— nincs perp' : coin.short ? '— kevés adat' : coin.loading ? '…' : '—'}
                        </span>}
                  </td>
                  <td className="scan-td">
                    {coin.rsi != null
                      ? <span className="scan-badge" style={{ color: st.color, background: 'rgba(136,136,136,0.12)' }}>{st.text}</span>
                      : <span className="muted">—</span>}
                  </td>
                  <td className={`scan-td right ${c24h >= 0 ? 'positive' : 'negative'}`}>{fmtPct(c24h)}</td>
                  <td className={`scan-td right ${c7d  >= 0 ? 'positive' : 'negative'}`}>{fmtPct(c7d)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <SourceAttr cg />
    </div>
  );
}

// ── Section 2: Volume Spike Detector ──────────────────────────────────────────

function VolumeScanner({ data, meta }) {
  const rows = useMemo(() =>
    data
      .map(c => ({ ...c, volRatio: c.market_cap > 0 ? c.total_volume / c.market_cap : 0 }))
      .sort((a, b) => b.volRatio - a.volRatio)
      .slice(0, 20),
  [data]);

  return (
    <div className="scan-section">
      <div className="scan-section-header">
        <span className="scan-section-title scan-section-title--vol">FORGALMI KIUGRÁS DETEKTOR<SourceDot source="CoinGecko" meta={meta} /></span>
        <span className="scan-section-sub">Top 20 a forgalom / piaci kap. arány szerint</span>
      </div>
      <div className="scan-table-wrap">
        <table className="scan-table">
          <thead>
            <tr>
              <th className="scan-th">Coin</th>
              <th className="scan-th right">Ár</th>
              <th className="scan-th right">24ó forgalom</th>
              <th className="scan-th right">Piaci kap.</th>
              <th className="scan-th right">Forg. / piaci kap.</th>
              <th className="scan-th">Aktivitás</th>
              <th className="scan-th right">24h%</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(coin => {
              const r      = coin.volRatio;
              const isSpike = r > 0.20;
              const isHigh  = r >= 0.15 && r <= 0.20;
              const c24h    = coin.price_change_percentage_24h ?? 0;
              return (
                <tr key={coin.id} className={`scan-row${isSpike ? ' vol-spike' : isHigh ? ' vol-high' : ''}`}>
                  <td className="scan-td"><CoinCell coin={coin} /></td>
                  <td className="scan-td right">{fmtPrice(coin.current_price)}</td>
                  <td className="scan-td right">{fmtMktCap(coin.total_volume)}</td>
                  <td className="scan-td right">{fmtMktCap(coin.market_cap)}</td>
                  <td className="scan-td right">
                    <span style={{ color: isSpike ? '#ff4d4d' : isHigh ? '#f5a623' : 'var(--text2)' }}>
                      {fmtNum(r * 100, 2)}%
                    </span>
                  </td>
                  <td className="scan-td">
                    {isSpike ? (
                      <span className="scan-badge" style={{ color: '#FF5000', background: 'rgba(255,80,0,0.2)', border: '1px solid rgba(255,80,0,0.5)' }}>🔥 Kiugrás</span>
                    ) : isHigh ? (
                      <span className="scan-badge" style={{ color: '#FFC800', background: 'rgba(255,200,0,0.2)', border: '1px solid rgba(255,200,0,0.5)' }}>⚡ Magas</span>
                    ) : (
                      <span className="scan-badge scan-badge-muted">Normál</span>
                    )}
                  </td>
                  <td className={`scan-td right ${c24h >= 0 ? 'positive' : 'negative'}`}>{fmtPct(c24h)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <SourceAttr cg />
    </div>
  );
}

// ── Section 3: BTC Correlation ────────────────────────────────────────────────

function BTCCorrelation({ coins, meta }) {
  const btc = coins.find(c => c.id === 'bitcoin');
  const btc7d = btc?.price_change_percentage_7d_in_currency ?? null;

  const rows = useMemo(() => {
    const btcPrices = btc?.sparkline_in_7d?.price;
    if (!btcPrices?.length) return [];
    return coins
      .filter(c => c.id !== 'bitcoin' && c.id !== 'ethereum' && !isStablecoin(c))
      .slice(0, 30)
      .map(c => ({ ...c, corr: btcCorrelation(c.sparkline_in_7d?.price, btcPrices) }))
      .sort((a, b) => (b.corr ?? -2) - (a.corr ?? -2));
  }, [coins, btc]);

  return (
    <div className="scan-section">
      <div className="scan-section-header">
        <span className="scan-section-title scan-section-title--btc">BTC KORRELÁCIÓ · 7 NAP<SourceDot source="CoinGecko (7 napos árgörbe)" meta={meta} /></span>
        {btc7d != null && (
          <span className="scan-section-sub">
            BTC 7d: <span className={btc7d >= 0 ? 'positive' : 'negative'}>{fmtPct(btc7d)}</span>
          </span>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="scan-state" style={{ padding: '16px 0' }}>Az árgörbék még töltődnek…</div>
      ) : (
        <div className="scan-table-wrap">
          <table className="scan-table">
            <thead>
              <tr>
                <th className="scan-th">Coin</th>
                <th className="scan-th right">Ár</th>
                <th className="scan-th right">7d%</th>
                <th className="scan-th right">Korreláció (r)</th>
                <th className="scan-th">Értelmezés</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(coin => {
                const lbl   = corrLabel(coin.corr);
                const alt7d = coin.price_change_percentage_7d_in_currency ?? 0;
                const barW  = coin.corr != null ? Math.max(0, coin.corr) * 100 : 0;
                return (
                  <tr key={coin.id} className="scan-row">
                    <td className="scan-td"><CoinCell coin={coin} /></td>
                    <td className="scan-td right">{fmtPrice(coin.current_price)}</td>
                    <td className={`scan-td right ${alt7d >= 0 ? 'positive' : 'negative'}`}>{fmtPct(alt7d)}</td>
                    <td className="scan-td right">
                      <div className="corr-cell">
                        <span className="corr-score" style={{ color: lbl.color }}>{coin.corr != null ? fmtNum(coin.corr, 2) : '—'}</span>
                        <div className="corr-bar-track">
                          <div className="corr-bar-fill" style={{ width: barW + '%', background: lbl.color }} />
                        </div>
                      </div>
                    </td>
                    <td className="scan-td">
                      <span className="scan-badge" style={{ color: lbl.color, background: 'rgba(136,136,136,0.12)' }}>
                        {lbl.text}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="scan-disclaimer">
        Pearson-korreláció az elmúlt 7 nap órás hozamaiból (r = 1: teljesen együtt mozog, 0: nincs lineáris kapcsolat). Ez nem pénzügyi tanácsadás.
      </div>
      <SourceAttr cg />
    </div>
  );
}

// ── Section 5: Stablecoin Dominance ──────────────────────────────────────────

const STABLE_IDS    = 'tether,usd-coin,dai,first-digital-usd,ethena-usds';
const STABLE_COLORS = { tether:'#26a17b','usd-coin':'#2775ca', dai:'#f5ac37','first-digital-usd':'#0033ad','ethena-usds':'#9b59b6' };
const STABLE_SYMS   = { tether:'USDT','usd-coin':'USDC', dai:'DAI','first-digital-usd':'FDUSD','ethena-usds':'USDS' };
const STABLE_REFRESH = 5 * 60 * 1000;

function fmtStableMcap(n) {
  if (n == null) return '—';
  return fmtCompact(n);
}

function StablecoinDominance() {
  const [stables,    setStables]    = useState([]);
  const [globalMcap, setGlobalMcap] = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [srcMeta,    setSrcMeta]    = useState(null);
  const cacheRef = useRef({ ts: 0 });

  const fetchStables = useCallback(async () => {
    if (Date.now() - cacheRef.current.ts < STABLE_REFRESH) return;
    const stabUrl = `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${STABLE_IDS}&order=market_cap_desc&price_change_percentage=24h`;
    const globUrl = 'https://api.coingecko.com/api/v3/global';
    try {
      const [stabRes, globRes] = await Promise.all([
        fetchWithCache(stabUrl, { cacheDuration: 60 * 60 * 1000 }),
        fetchWithCache(globUrl, { cacheDuration: 60 * 60 * 1000 }),
      ]);
      setStables(Array.isArray(stabRes) ? stabRes : []);
      setGlobalMcap(globRes?.data?.total_market_cap?.usd ?? null);
      setSrcMeta(mergeMeta(getFetchMeta(stabUrl), getFetchMeta(globUrl)));
      cacheRef.current.ts = Date.now();
    } catch (_) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStables(); }, [fetchStables]);
  useEffect(() => {
    const id = setInterval(fetchStables, STABLE_REFRESH);
    return () => clearInterval(id);
  }, [fetchStables]);

  const totalStable   = stables.reduce((s, c) => s + (c.market_cap ?? 0), 0);
  const usdtMcap      = stables.find(c => c.id === 'tether')?.market_cap ?? 0;
  const usdtShare     = totalStable > 0 ? (usdtMcap / totalStable) * 100 : null;
  const stableDom     = globalMcap  > 0 ? (totalStable / globalMcap) * 100 : null;
  const total24hChg   = stables.reduce((s, c) => {
    const rate = (c.price_change_percentage_24h ?? 0) / 100;
    return s + (c.market_cap ?? 0) * rate;
  }, 0);
  const maxMcap = stables.length > 0 ? Math.max(...stables.map(c => c.market_cap ?? 0)) : 1;

  function signal() {
    if (total24hChg > 1e9)  return { text: '🟢 Nagy stablecoin beáramlás — potenciális kripto vásárlóerő', color: '#00c853' };
    if (total24hChg > 5e8)  return { text: '🟡 Mérsékelt stablecoin növekedés', color: '#f5a623' };
    if (total24hChg < -5e8) return { text: '🔴 Stablecoin kiáramlás — tőke elhagyja a kriptót', color: '#ff4d4d' };
    return                         { text: '⚪ Stablecoin piac stabil', color: 'var(--text3)' };
  }

  const sig = signal();

  return (
    <div className="scan-section">
      <div className="scan-section-header">
        <span className="scan-section-title scan-section-title--stable">STABLECOIN DOMINANCIA KÖVETŐ<SourceDot source="CoinGecko" meta={srcMeta} /></span>
        <button className="scan-refresh-btn" onClick={() => { cacheRef.current.ts=0; fetchStables(); }}>↻</button>
      </div>
      <div className="scan-section-sub" style={{ marginBottom: 12, fontSize: 11 }}>
        A stablecoinok piaci kapitalizációjának növekedése potenciális kripto-beáramlást jelez
      </div>

      {loading ? (
        <div className="scan-state" style={{ padding: '20px 0' }}>
          <div className="scan-spinner">◈</div>
          <div>Stablecoin adatok betöltése…</div>
        </div>
      ) : (
        <>
          <div className="stable-cards">
            <div className="stable-card">
              <div className="stable-card-label">Összes stablecoin piaci kap.</div>
              <div className="stable-card-val">{fmtStableMcap(totalStable)}</div>
              <div className={`stable-card-sub ${total24hChg >= 0 ? 'positive' : 'negative'}`}>
                {total24hChg >= 0 ? '+' : ''}{fmtStableMcap(Math.abs(total24hChg))} 24h
              </div>
            </div>
            <div className="stable-card">
              <div className="stable-card-label">USDT Dominancia</div>
              <div className="stable-card-val">{usdtShare != null ? `${fmtNum(usdtShare, 1)}%` : '—'}</div>
              <div className="stable-card-sub">{fmtStableMcap(usdtMcap)} USDT</div>
            </div>
            <div className="stable-card">
              <div className="stable-card-label">Stablecoin / kripto arány</div>
              <div className="stable-card-val">{stableDom != null ? `${fmtNum(stableDom, 2)}%` : '—'}</div>
              <div className="stable-card-sub">a teljes kriptopiacból</div>
            </div>
            <div className="stable-card">
              <div className="stable-card-label">24h Változás</div>
              <div className={`stable-card-val ${total24hChg >= 0 ? 'positive' : 'negative'}`}>
                {total24hChg >= 0 ? '+' : ''}{fmtStableMcap(Math.abs(total24hChg))}
              </div>
              <div className="stable-card-sub">összes stablecoin</div>
            </div>
          </div>

          <div className="scan-table-wrap" style={{ marginBottom: 14 }}>
            <table className="scan-table">
              <thead>
                <tr>
                  <th className="scan-th">Coin</th>
                  <th className="scan-th right">Piaci kap.</th>
                  <th className="scan-th right">24h Változás</th>
                  <th className="scan-th right">Kripto piaci kap. %-a</th>
                  <th className="scan-th">Arány</th>
                </tr>
              </thead>
              <tbody>
                {stables.map(c => {
                  const chg   = c.price_change_percentage_24h ?? null;
                  const share = globalMcap > 0 ? (c.market_cap / globalMcap) * 100 : null;
                  const barW  = maxMcap > 0 ? (c.market_cap / maxMcap) * 100 : 0;
                  const col   = STABLE_COLORS[c.id] ?? '#888';
                  return (
                    <tr key={c.id} className="scan-row">
                      <td className="scan-td">
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <span style={{ width:10, height:10, borderRadius:'50%', background:col, display:'inline-block', flexShrink:0 }} />
                          <span style={{ fontWeight:700 }}>{STABLE_SYMS[c.id] ?? c.symbol.toUpperCase()}</span>
                        </div>
                      </td>
                      <td className="scan-td right" style={{ fontWeight:700 }}>{fmtStableMcap(c.market_cap)}</td>
                      <td className={`scan-td right ${chg == null ? '' : chg >= 0 ? 'positive' : 'negative'}`}>
                        {chg != null ? `${fmtPct(chg, 2)}` : '—'}
                      </td>
                      <td className="scan-td right">{share != null ? `${fmtNum(share, 3)}%` : '—'}</td>
                      <td className="scan-td" style={{ minWidth: 100 }}>
                        <div className="stable-bar-track">
                          <div className="stable-bar-fill" style={{ width:`${barW}%`, background:col }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="stable-signal-box" style={{ borderColor: sig.color }}>
            <div className="stable-signal-title">📊 Stablecoin Szignál értelmezése:</div>
            <div style={{ color: sig.color, fontWeight: 600, fontSize: 12, marginTop: 4 }}>{sig.text}</div>
          </div>
        </>
      )}
      <SourceAttr cg />
    </div>
  );
}

// ── Section 6: ATH Proximity Radar ───────────────────────────────────────────

function ATHRadar({ data, meta }) {
  const [showAll, setShowAll] = useState(false);

  const rows = useMemo(() => {
    return data
      .filter(c => !isStablecoin(c) && c.ath > 0 && c.current_price > 0)
      .map(c => {
        const distPct    = ((c.current_price - c.ath) / c.ath) * 100;
        const nearnessPct = 100 + distPct;
        return { ...c, distPct, nearnessPct };
      })
      .sort((a, b) => b.nearnessPct - a.nearnessPct);
  }, [data]);

  const displayRows = showAll ? rows : rows.slice(0, 20);
  const near5   = rows.filter(r => r.distPct >= -5).length;
  const near20  = rows.filter(r => r.distPct >= -20).length;
  const near50  = rows.filter(r => r.distPct >= -50).length;
  const avgDist = rows.length > 0 ? rows.reduce((s, r) => s + r.distPct, 0) / rows.length : 0;
  const nearCoins = rows.filter(r => r.distPct >= -20);

  function distColor(d) {
    if (d >= -20) return '#00c853';
    if (d >= -50) return '#f5a623';
    if (d >= -80) return '#ff6600';
    return '#ff4d4d';
  }

  return (
    <div className="scan-section">
      <div className="scan-section-header">
        <span className="scan-section-title scan-section-title--ath">ATH KÖZELSÉG RADAR<SourceDot source="CoinGecko" meta={meta} /></span>
        <span className="scan-section-sub">Melyik coin van a legközelebb historikus csúcsához?</span>
      </div>

      {/* Section A: near-ATH pills */}
      <div className="ath-near-wrap">
        <div className="ath-near-title">🏆 ATH Közeliek (0–20% alatt)</div>
        <div className="ath-near-pills">
          {nearCoins.length === 0
            ? <span className="ath-empty">Jelenleg nincs coin 20%-on belül az ATH-tól</span>
            : nearCoins.map(c => (
                <span key={c.id} className="ath-pill" style={{ borderColor: distColor(c.distPct) }}>
                  <span style={{ fontWeight:700, color:'var(--text)' }}>{c.symbol.toUpperCase()}</span>
                  {' '}
                  <span style={{ color: distColor(c.distPct) }}>{fmtNum(c.distPct, 1)}%</span>
                </span>
              ))
          }
        </div>
      </div>

      {/* Section B: sortable table */}
      <div className="scan-table-wrap" style={{ marginBottom: 12 }}>
        <table className="scan-table scan-table--rank">
          <thead>
            <tr>
              <th className="scan-th">#</th>
              <th className="scan-th">Coin</th>
              <th className="scan-th right">Jelenlegi Ár</th>
              <th className="scan-th right">ATH</th>
              <th className="scan-th right">ATH Távolság</th>
              <th className="scan-th right">Közelség %</th>
              <th className="scan-th">Sáv</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((coin, i) => {
              const col = distColor(coin.distPct);
              const barW = Math.max(0, Math.min(100, coin.nearnessPct));
              return (
                <tr key={coin.id} className="scan-row">
                  <td className="scan-td muted">{i + 1}</td>
                  <td className="scan-td"><CoinCell coin={coin} /></td>
                  <td className="scan-td right">{fmtPrice(coin.current_price)}</td>
                  <td className="scan-td right" style={{ color:'var(--text3)' }}>{fmtPrice(coin.ath)}</td>
                  <td className="scan-td right">
                    <span style={{ color: col, fontWeight: 700 }}>{fmtNum(coin.distPct, 1)}%</span>
                  </td>
                  <td className="scan-td right">
                    <span style={{ color: col, fontWeight: 700 }}>{fmtNum(coin.nearnessPct, 1)}%</span>
                  </td>
                  <td className="scan-td" style={{ minWidth: 90 }}>
                    <div className="ath-bar-track">
                      <div className="ath-bar-fill" style={{ width:`${barW}%`, background: col }} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <button className="scan-showall-btn" onClick={() => setShowAll(v => !v)}>
        {showAll ? `Top 20 mutatása` : `Összes ${rows.length} coin mutatása`}
      </button>

      {/* Section C: stats */}
      <div className="ath-stats-grid">
        <div className="ath-stat"><div className="ath-stat-val positive">{near5}</div><div className="ath-stat-lbl">ATH-n lévő (≤5%)</div></div>
        <div className="ath-stat"><div className="ath-stat-val positive">{near20}</div><div className="ath-stat-lbl">20%-on belül</div></div>
        <div className="ath-stat"><div className="ath-stat-val" style={{color:'#f5a623'}}>{near50}</div><div className="ath-stat-lbl">50%-on belül</div></div>
        <div className="ath-stat"><div className="ath-stat-val negative">{fmtNum(avgDist, 1)}%</div><div className="ath-stat-lbl">Átl. ATH távolság</div></div>
      </div>
      <SourceAttr cg />
    </div>
  );
}

// ── Main Export ────────────────────────────────────────────────────────────────

export default function ScannerTab({ data, loading, error, lastUpdated, onRefresh, meta, coins, coinsMeta, preview = false }) {
  if (loading || (!data && !error)) {
    return (
      <div className="scan-state">
        <div className="scan-spinner">◈</div>
        <div>{loading ? 'Top 100 coin betöltése…' : 'Szkenner inicializálása…'}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="scan-state scan-state-error">
        <span>⚠ {error}</span>
        <button className="scan-refresh-btn" onClick={onRefresh}>Újrapróbálom</button>
      </div>
    );
  }

  return (
    <div className="scanner-tab">
      <div className="scanner-toolbar">
        <span className="scanner-info">
          {data.length} coin<SourceDot source="CoinGecko" meta={meta ?? (lastUpdated ? { at: +lastUpdated } : null)} />
        </span>
        <button className="scan-refresh-btn" onClick={onRefresh}>↻ Frissítés</button>
      </div>

      <GainersLosers      data={data} meta={meta} />
      <StablecoinDominance />
      <RSIScanner          data={data} preview={preview} />
      <VolumeScanner       data={data} meta={meta} />
      <BTCCorrelation      coins={coins ?? []} meta={coinsMeta} />
      <ATHRadar            data={data} meta={meta} />
      <DeFiTVL />
    </div>
  );
}
